import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Button, Checkbox, Empty, Table, Tag, Tooltip } from "antd";
import dayjs from "dayjs";

import {
  COLLECTIONS_STAGE_LABELS,
  COLLECTIONS_STAGES_ON_HOLD,
  CollectionsStage,
  type ProductClientCollections,
} from "@fitness-app/data-models/entities/ProductClient";

import { getPriceFormatter } from "~/modules/Products/utils/getPriceFormatter";
import ConfirmPaymentModal from "~/modules/Products/Product/ProductOrders/ConfirmPaymentModal";
import {
  useConfirmManualPayment,
  type ConfirmPaymentDocumentIssuance,
} from "~/modules/Products/hooks/useConfirmManualPayment";

import CollectionsStageModal from "./CollectionsStageModal";
import { type OverdueInstallmentItem } from "./useInstallmentCollectionStats";
import { useUpdateCollectionsStage } from "./useUpdateCollectionsStage";

const revenueFormatter = getPriceFormatter("PLN");
const formatAmount = (cents: number | null) => (cents === null ? "—" : revenueFormatter.format(cents / 100));

/** One collections case: every unpaid rata of one client in one product. */
export interface OverdueClientCase {
  key: string;
  productClientId: string;
  clientEmail: string;
  clientName: string | null;
  clientStatus: OverdueInstallmentItem["clientStatus"];
  productId: string;
  collections: ProductClientCollections | null;
  plan: OverdueInstallmentItem["plan"];
  invoices: OverdueInstallmentItem[];
  /** Actionable debt: manual past due + card exhausted. */
  overdueAmount: number;
  overdueCount: number;
  retryingCount: number;
  maxDaysOverdue: number;
}

export const groupOverdueByClient = (items: OverdueInstallmentItem[]): OverdueClientCase[] => {
  const cases = new Map<string, OverdueClientCase>();

  for (const item of items) {
    const key = `${item.productId}:${item.clientId}`;
    const current = cases.get(key) ?? {
      key,
      productClientId: item.clientId,
      clientEmail: item.clientEmail,
      clientName: item.clientName,
      clientStatus: item.clientStatus,
      productId: item.productId,
      collections: item.collections,
      plan: item.plan,
      invoices: [],
      overdueAmount: 0,
      overdueCount: 0,
      retryingCount: 0,
      maxDaysOverdue: 0,
    };
    cases.set(key, current);

    current.invoices.push(item);
    if (item.source === "card_retrying") {
      current.retryingCount += 1;
    } else {
      current.overdueCount += 1;
      current.overdueAmount += item.amount ?? 0;
      current.maxDaysOverdue = Math.max(current.maxDaysOverdue, item.daysOverdue);
    }
  }

  return Array.from(cases.values())
    .map((entry) => ({ ...entry, invoices: entry.invoices.sort((a, b) => b.daysOverdue - a.daysOverdue) }))
    .sort((a, b) => b.overdueAmount - a.overdueAmount || b.maxDaysOverdue - a.maxDaysOverdue);
};

const stageColor = (stage: CollectionsStage | undefined): string => {
  switch (stage) {
    case CollectionsStage.Reminder:
      return "gold";
    case CollectionsStage.Demand:
    case CollectionsStage.FinalDemand:
      return "volcano";
    case CollectionsStage.ExternalCollection:
      return "red";
    case CollectionsStage.Arrangement:
      return "blue";
    case CollectionsStage.Recovered:
      return "green";
    case CollectionsStage.WrittenOff:
    case CollectionsStage.Resignation:
      return "default";
    default:
      return "orange";
  }
};

const SourceTag = ({ row }: { row: OverdueInstallmentItem }) => {
  if (row.source === "manual_invoice") {
    return <Tag color="orange">Faktura manual</Tag>;
  }

  if (row.source === "card_retrying") {
    return (
      <Tooltip
        title={`Stripe spróbuje ponownie ${row.nextPaymentAttemptAt ? dayjs(row.nextPaymentAttemptAt).format("DD.MM.YYYY") : "wkrótce"} (próba ${(row.attemptCount ?? 0) + 1})`}
      >
        <Tag color="gold">Karta – ponawiana</Tag>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={`Stripe wyczerpał ${row.attemptCount ?? "wszystkie"} prób(y) — nic już nie pobierze samo`}>
      <Tag color="red">Karta – wyczerpana</Tag>
    </Tooltip>
  );
};

interface OverdueClientsTableProps {
  items: OverdueInstallmentItem[];
}

/**
 * The collections worklist: one row per client, her unpaid raty underneath,
 * and the two things Anita used to do in a spreadsheet — mark a rata as paid
 * (bank transfer, Tpay, Fakturownia) and move the case to the next stage.
 */
const OverdueClientsTable = ({ items }: OverdueClientsTableProps) => {
  const queryClient = useQueryClient();
  const [showRetrying, setShowRetrying] = useState(false);
  const [showClosed, setShowClosed] = useState(false);
  const [stageTarget, setStageTarget] = useState<OverdueClientCase | null>(null);
  const [paymentTarget, setPaymentTarget] = useState<OverdueInstallmentItem | null>(null);

  const confirmPayment = useConfirmManualPayment();
  const updateStage = useUpdateCollectionsStage();

  const cases = useMemo(() => {
    const grouped = groupOverdueByClient(showRetrying ? items : items.filter((item) => item.source !== "card_retrying"));
    return showClosed
      ? grouped
      : grouped.filter(
          (entry) =>
            !entry.collections ||
            !COLLECTIONS_STAGES_ON_HOLD.includes(entry.collections.stage) ||
            entry.collections.stage === CollectionsStage.Arrangement,
        );
  }, [items, showRetrying, showClosed]);

  const handleConfirmPayment = async (documentIssuance?: ConfirmPaymentDocumentIssuance) => {
    if (!paymentTarget) {
      return;
    }

    try {
      await confirmPayment.mutateAsync({
        productId: paymentTarget.productId,
        orderId: paymentTarget.orderId,
        clientEmail: paymentTarget.clientEmail,
        documentIssuance,
      });
      void queryClient.invalidateQueries(["installmentCollectionStats"]);
    } finally {
      setPaymentTarget(null);
    }
  };

  const columns = [
    {
      title: "Klientka",
      key: "client",
      render: (_: unknown, row: OverdueClientCase) => (
        <div>
          <p className="font-medium">
            {row.clientName ?? "—"}
            {row.clientStatus === "archived" ? (
              <Tag className="ml-2" color="default">
                Zarchiwizowana
              </Tag>
            ) : null}
          </p>
          <p className="text-xs text-gray-500">{row.clientEmail}</p>
        </div>
      ),
    },
    {
      title: "Zaległość",
      key: "debt",
      sorter: (a: OverdueClientCase, b: OverdueClientCase) => a.overdueAmount - b.overdueAmount,
      render: (_: unknown, row: OverdueClientCase) => (
        <div>
          <p className="font-medium text-rose-600">{formatAmount(row.overdueAmount)}</p>
          <p className="text-xs text-gray-500">
            {row.overdueCount} {row.overdueCount === 1 ? "faktura" : "faktury"}
            {row.retryingCount ? ` · ${row.retryingCount} w ponowieniu` : ""}
          </p>
        </div>
      ),
    },
    {
      title: "Najstarsza",
      dataIndex: "maxDaysOverdue",
      key: "maxDaysOverdue",
      sorter: (a: OverdueClientCase, b: OverdueClientCase) => a.maxDaysOverdue - b.maxDaysOverdue,
      render: (days: number) => <span className={days > 30 ? "font-medium text-rose-600" : ""}>{days} dni</span>,
    },
    {
      title: "Plan",
      key: "plan",
      render: (_: unknown, row: OverdueClientCase) =>
        row.plan.installments ? (
          <div className="whitespace-nowrap">
            <p>
              rata {row.plan.cycleNumber ?? "?"} z {row.plan.installments}
            </p>
            {row.plan.installmentEndsAt ? (
              <p className="text-xs text-gray-500">do {dayjs(row.plan.installmentEndsAt).format("DD.MM.YYYY")}</p>
            ) : null}
          </div>
        ) : (
          "—"
        ),
    },
    {
      title: "Etap",
      key: "stage",
      filters: Object.values(CollectionsStage).map((stage) => ({ text: COLLECTIONS_STAGE_LABELS[stage], value: stage })),
      onFilter: (value: unknown, row: OverdueClientCase) => (row.collections?.stage ?? CollectionsStage.New) === value,
      render: (_: unknown, row: OverdueClientCase) => {
        const stage = row.collections?.stage ?? CollectionsStage.New;
        return (
          <div>
            <Tag color={stageColor(stage)}>{COLLECTIONS_STAGE_LABELS[stage]}</Tag>
            {row.collections?.nextContactAt ? (
              <p className="mt-1 text-xs text-gray-500">
                kontakt {dayjs(row.collections.nextContactAt).format("DD.MM.YYYY")}
              </p>
            ) : null}
          </div>
        );
      },
    },
    {
      title: "",
      key: "actions",
      fixed: "right" as const,
      render: (_: unknown, row: OverdueClientCase) => (
        <Button size="small" onClick={() => setStageTarget(row)}>
          Zmień etap
        </Button>
      ),
    },
  ];

  const invoiceColumns = [
    { title: "Rata", dataIndex: "paymentCycle", key: "paymentCycle", render: (cycle: number | null) => cycle ?? "—" },
    { title: "Kwota", dataIndex: "amount", key: "amount", render: (amount: number | null) => formatAmount(amount) },
    {
      title: "Termin",
      dataIndex: "dueDate",
      key: "dueDate",
      render: (dueDate: string) => dayjs(dueDate).format("DD.MM.YYYY"),
    },
    {
      title: "Dni po terminie",
      dataIndex: "daysOverdue",
      key: "daysOverdue",
      render: (days: number) => <span className={days > 30 ? "font-medium text-rose-600" : ""}>{days}</span>,
    },
    { title: "Źródło", key: "source", render: (_: unknown, row: OverdueInstallmentItem) => <SourceTag row={row} /> },
    {
      title: "Faktura",
      dataIndex: "invoiceId",
      key: "invoiceId",
      render: (invoiceId: string | null) =>
        invoiceId ? (
          <a href={`https://dashboard.stripe.com/invoices/${invoiceId}`} target="_blank" rel="noreferrer">
            Otwórz w Stripe
          </a>
        ) : (
          "—"
        ),
    },
    {
      title: "",
      key: "actions",
      render: (_: unknown, row: OverdueInstallmentItem) =>
        row.source === "card_retrying" ? null : (
          <Tooltip title="Przelew na konto, Tpay, Fakturownia — faktura w Stripe zostanie oznaczona jako opłacona poza systemem">
            <Button size="small" type="link" onClick={() => setPaymentTarget(row)}>
              Oznacz jako opłacone
            </Button>
          </Tooltip>
        ),
    },
  ];

  return (
    <>
      <div className="mt-3 flex flex-wrap gap-4">
        <Checkbox checked={showRetrying} onChange={(event) => setShowRetrying(event.target.checked)}>
          Pokaż raty w trakcie ponowień Stripe
        </Checkbox>
        <Checkbox checked={showClosed} onChange={(event) => setShowClosed(event.target.checked)}>
          Pokaż sprawy zamknięte (odzyskane, umorzone, rezygnacje)
        </Checkbox>
      </div>

      {cases.length ? (
        <Table
          className="mt-4"
          size="small"
          rowKey="key"
          columns={columns}
          dataSource={cases}
          pagination={{ pageSize: 20, hideOnSinglePage: true }}
          scroll={{ x: true }}
          expandable={{
            expandedRowRender: (row: OverdueClientCase) => (
              <Table
                size="small"
                rowKey="orderId"
                columns={invoiceColumns}
                dataSource={row.invoices}
                pagination={false}
              />
            ),
          }}
        />
      ) : (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Brak zaległości 🎉" className="py-10" />
      )}

      <CollectionsStageModal
        open={stageTarget !== null}
        loading={updateStage.isLoading}
        client={
          stageTarget
            ? {
                productClientId: stageTarget.productClientId,
                clientName: stageTarget.clientName,
                clientEmail: stageTarget.clientEmail,
              }
            : null
        }
        current={stageTarget?.collections ?? null}
        onCancel={() => setStageTarget(null)}
        onSubmit={(payload) => updateStage.mutate(payload, { onSuccess: () => setStageTarget(null) })}
      />

      <ConfirmPaymentModal
        open={paymentTarget !== null}
        loading={confirmPayment.isLoading}
        onCancel={() => setPaymentTarget(null)}
        onConfirm={(documentIssuance) => void handleConfirmPayment(documentIssuance)}
      />
    </>
  );
};

export default OverdueClientsTable;
